/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/// <reference types="bingmaps" />

import * as Msdyn365 from '@msdyn365-commerce/core';
import { OrgUnitLocation } from '@msdyn365-commerce/retail-proxy';
import { isStoreInfoLocation } from '@msdyn365-commerce-modules/bopis-utilities';
import { IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import classname from 'classnames';
import { observable, reaction, when } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import {
    ArrayExtensions,
    CrossEntityNodeType,
    getCrossEntityNodeKey,
    ICrossEntityInventoryNode
} from '@msdyn365-commerce-modules/retail-actions';
import { IMapData } from './map.data';
import { IMapProps, IPushpinOptionsData } from './map.props.autogenerated';

/**
 * Header Interface.
 */
export interface IHeader {
    headerProps: INodeProps;
    heading: React.ReactNode;
}

/**
 * MapViewProps Interface.
 */
export interface IMapViewProps extends IMapProps<IMapData> {
    ModuleProps: IModuleProps;
    Header: IHeader;
    MapProps: INodeProps;
    Map: Microsoft.Maps.Map | undefined;
}

/**
 *
 * Map component.
 * @extends {React.Component<IMapProps<IMapData>>}
 */
@observer
class Map extends React.Component<IMapProps<IMapData>> {
    @observable public map: Microsoft.Maps.Map | undefined;
    private timeout: number = 500;

    private readonly mapRef: React.RefObject<HTMLElement> = React.createRef<HTMLElement>();

    public constructor(props: IMapProps<IMapData>) {
        super(props);
    }

    public componentDidMount(): void {
        const {
            context: {
                telemetry,
                actionContext: {
                    requestContext: { channel }
                }
            }
        } = this.props;

        if (channel && !channel.BingMapsApiKey) {
            telemetry.error('BingMapsApiKey is missing.');
            return;
        }

        if (channel && !channel.BingMapsEnabled) {
            telemetry.error('Map is disabled from HQ.');
            return;
        }

        const loadMapAPIInput = {
            key: channel?.BingMapsApiKey,
            lang: this.props.context.actionContext.requestContext.locale,
            market: this.props.context?.actionContext?.requestContext?.channel?.ChannelCountryRegionISOCode
        };

        if (
            this.props.data.storeSelectorStateManager?.result?.isMapApiLoaded ||
            this.props.data.distributorSelectorStateManager?.result?.isMapApiLoaded
        ) {
            this._initMap();
            if (this.props.data.distributorSelectorStateManager?.result?.isDistributorSelectorDialogOpen) {
                this._updateMapsForDistributor();
            } else if (this._isWarehouseMapActive()) {
                this._updateMapForWarehouses();
            } else {
                this._updateMap();
            }
        }

        when(
            () => !!this.props.data.storeSelectorStateManager?.result?.setMapModuleLoaded,
            () => {
                this.props.data.storeSelectorStateManager?.result?.setMapModuleLoaded(true);
            }
        );

        when(
            () => !!this.props.data.distributorSelectorStateManager?.result?.setMapModuleLoaded,
            () => {
                this.props.data.distributorSelectorStateManager?.result?.setMapModuleLoaded(true);
            }
        );

        // `when` rather than `reaction`: a state manager that has already resolved by the time the
        // module mounts would never fire a reaction, and unlike the store selector - which also
        // calls loadMapApi itself when its dialog opens - a page whose only map consumer is the
        // warehouse locator has no second trigger, so the Bing SDK would never load at all.
        when(
            () =>
                !!this.props.data.storeSelectorStateManager?.result?.loadMapApi ||
                !!this.props.data.distributorSelectorStateManager?.result?.loadMapApi,
            () => {
                if (this.props.data.distributorSelectorStateManager?.result?.loadMapApi) {
                    this.props.data.distributorSelectorStateManager?.result?.loadMapApi(loadMapAPIInput);
                } else {
                    this.props.data.storeSelectorStateManager?.result?.loadMapApi(loadMapAPIInput);
                }
            }
        );

        when(
            () =>
                !!this.props.data.storeSelectorStateManager?.result?.isMapApiLoaded ||
                !!this.props.data.distributorSelectorStateManager?.result?.isMapApiLoaded,
            () => {
                this._initMap();
            }
        );

        reaction(
            () => {
                return [
                    this.props.data.storeSelectorStateManager.result?.context?.orgUnitStoreInformation,
                    this.props.data.storeSelectorStateManager.result?.selectedStoreLocationId,
                    this.map
                ];
            },
            () => {
                this._updateMap();
            }
        );

        reaction(
            () => {
                return [
                    this.props.data.distributorSelectorStateManager?.result?.distributorList,
                    this.props.data.distributorSelectorStateManager?.result?.selectedDistributorId,
                    this.map
                ];
            },
            () => {
                this._updateMapsForDistributor();
            }
        );

        reaction(
            () => {
                return [
                    this.props.data.warehouseLocatorStateManager?.result?.nodes,
                    this.props.data.warehouseLocatorStateManager?.result?.selectedNodeKey,
                    this.map
                ];
            },
            () => {
                this._updateMapForWarehouses();
            }
        );
    }

    public render(): JSX.Element | null {
        const {
            config: { heading, className },
            data
        } = this.props;

        const shouldDisplayMap = data.storeSelectorStateManager?.result?.isDialogOpen
            ? data.storeSelectorStateManager?.result?.listMapViewState.displayMap
            : data.distributorSelectorStateManager?.result?.isDistributorSelectorDialogOpen
            ? data.distributorSelectorStateManager?.result?.listMapViewState.displayMap
            : // The warehouse locator renders the map in a dedicated column rather than behind a
            // list/map toggle, so it is shown whenever there is something to plot.
            this._isWarehouseMapActive()
            ? true
            : false;

        const viewProps: IMapViewProps = {
            ...this.props,
            ModuleProps: {
                tag: 'div',
                moduleProps: this.props,
                className: classname('ms-map', { show: shouldDisplayMap }, className)
            },
            Header: {
                headerProps: { className: 'ms-map__header' },
                heading: heading?.text && (
                    <Msdyn365.Text
                        className='ms-map__heading'
                        {...heading}
                        tag={heading.tag || 'h2'}
                        text={heading.text}
                        editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                    />
                )
            },
            MapProps: {
                tag: 'div',
                className: 'ms-map__body',
                ref: this.mapRef,
                onKeyUp: this._onMapKeyUp
            },
            Map: this.map
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * Method to be called on heading change.
     * @param event -Content Edit Event.
     */
    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent): void => {
        this.props.config.heading!.text = event.target.value;
    };

    /**
     * Handles Escape key inside the map container.
     * When the Bing Maps "Map type" dropdown is open (indicated by the
     * `selected` class on #MapStyleSelector), clicks the button to close
     * it and stops the event from also closing the parent Store Locator
     * dialog. When no dropdown is open, the event propagates normally
     * so the dialog can close.
     */
    private readonly _onMapKeyUp = (e: React.KeyboardEvent): void => {
        if (e.key === 'Escape') {
            const mapBody = this.mapRef?.current;
            if (mapBody) {
                const openSelector = mapBody.querySelector('#MapStyleSelector.selected');
                if (openSelector instanceof HTMLElement) {
                    openSelector.click();
                    e.stopPropagation();
                }
            }
        }
    };

    /**
     * Initialize the map.
     */
    private readonly _initMap = () => {
        if (this.map) {
            // Both componentDidMount and the map-API `when` can reach here; only one control is wanted.
            return;
        }

        const {
            context: {
                actionContext: {
                    requestContext: { channel }
                }
            },
            data
        } = this.props;

        if (data.storeSelectorStateManager?.result?.isMapApiLoaded || data.distributorSelectorStateManager?.result?.isMapApiLoaded) {
            const mapLoadOptions = {
                credentials: channel?.BingMapsApiKey,
                pushpinAccessible: true
            };
            this.map = new Microsoft.Maps.Map(this.mapRef.current as HTMLElement, mapLoadOptions);
            Microsoft.Maps.Events.addHandler(this.map, 'click', async () => {
                if (data.distributorSelectorStateManager?.result?.isDistributorSelectorDialogOpen) {
                    // Hide the distributor details that appear below the screen size of 768px.
                    data.distributorSelectorStateManager?.result?.updateDistributorMapVisibility(false);
                    await data.distributorSelectorStateManager?.result?.setSelectedDistributorId(undefined);
                } else if (this._isWarehouseMapActive()) {
                    await data.warehouseLocatorStateManager?.result?.setSelectedNodeKey(undefined);
                } else {
                    await data.storeSelectorStateManager?.result?.setSelectedStoreLocationId(undefined);
                }
            });
        }
    };

    private readonly _updateMapsForDistributor = () => {
        const pushpinOptions = this.props.config.pushpinOptions;
        const distributorSelectorStateManager = this.props.data.distributorSelectorStateManager?.result;
        const distributorInfoList = distributorSelectorStateManager?.distributorList;
        const selectedDistributorId = distributorSelectorStateManager?.selectedDistributorId;

        if (this.map && ArrayExtensions.hasElements(distributorInfoList)) {
            let mapLocation: OrgUnitLocation | undefined;
            this.map.entities.clear();
            const pushpins: Microsoft.Maps.IPrimitive[] = [];

            const distributorList = distributorInfoList.filter(distributor => distributor.OrgUnit !== undefined);

            for (const [index, distributorInfo] of distributorList.entries()) {
                const orgUnit = distributorInfo.OrgUnit;

                if (orgUnit?.Latitude && orgUnit.Longitude) {
                    const isLocationSelected = selectedDistributorId === orgUnit?.RecordId;
                    const options = this._getPushpinOptions(isLocationSelected, index, pushpinOptions, orgUnit.OrgUnitName);
                    const pushpin = new Microsoft.Maps.Pushpin(new Microsoft.Maps.Location(orgUnit?.Latitude, orgUnit.Longitude), options);

                    Microsoft.Maps.Events.addHandler(pushpin, 'click', () => {
                        this.handleDistributorPushpinClick(orgUnit.RecordId);
                    });
                    isLocationSelected ? pushpins.unshift(pushpin) : pushpins.push(pushpin);

                    if (isLocationSelected) {
                        mapLocation = orgUnit;
                    }
                }
            }
            if (mapLocation) {
                const currentLocation = new Microsoft.Maps.Location(mapLocation.Latitude, mapLocation.Longitude);
                this.map.setView({ center: currentLocation });
            } else {
                this.map.setView({
                    bounds: Microsoft.Maps.LocationRect?.fromShapes(pushpins)
                });
            }

            this.map.setOptions({ pushpinAccessible: true } as Microsoft.Maps.IMapOptions);
            this.map.entities.push(pushpins);
        }
    };

    /**
     * Update the map.
     */
    private readonly _updateMap = () => {
        const pushpinOptions = this.props.config.pushpinOptions;
        const storeSelectorStateManager = this.props.data.storeSelectorStateManager?.result;
        const orgUnitStoreInformation = storeSelectorStateManager?.context?.orgUnitStoreInformation;
        const selectedStoreLocationId = storeSelectorStateManager?.selectedStoreLocationId;
        const selectedStore = storeSelectorStateManager?.selectedStore ?? { StoreId: selectedStoreLocationId };

        if (this.map && orgUnitStoreInformation) {
            let mapLocation: OrgUnitLocation | undefined;
            this.map.entities.clear();
            const pushpins: Microsoft.Maps.IPrimitive[] = [];

            const storeLocationList = orgUnitStoreInformation.filter(store => store.OrgUnitAvailability !== undefined);

            for (const [index, unitStoreInfo] of storeLocationList.entries()) {
                const storeLocation = unitStoreInfo.OrgUnitAvailability?.OrgUnitLocation;

                if (storeLocation?.Latitude && storeLocation.Longitude) {
                    const isSelectedLocation = isStoreInfoLocation(storeLocation, selectedStore);
                    const options = this._getPushpinOptions(isSelectedLocation, index, pushpinOptions, storeLocation.OrgUnitName);
                    const pushpin = new Microsoft.Maps.Pushpin(
                        new Microsoft.Maps.Location(storeLocation.Latitude, storeLocation.Longitude),
                        options
                    );

                    Microsoft.Maps.Events.addHandler(pushpin, 'click', () => {
                        this.handleClickEvent(storeLocation);
                    });
                    isSelectedLocation ? pushpins.unshift(pushpin) : pushpins.push(pushpin);

                    if (isSelectedLocation) {
                        mapLocation = storeLocation;
                    }
                }
            }

            if (mapLocation) {
                const currentLocation = new Microsoft.Maps.Location(mapLocation.Latitude, mapLocation.Longitude);
                this.map.setView({ center: currentLocation });
            } else {
                // Create a LocationRect from array of pushpins and set the map view.
                this.map.setView({
                    bounds: Microsoft.Maps.LocationRect?.fromShapes(pushpins)
                });
            }

            this.map.setOptions({ pushpinAccessible: true } as Microsoft.Maps.IMapOptions);
            this.map.entities.push(pushpins);
        }
    };

    /**
     * Plot the warehouse-locator results.
     *
     * Marker icons represent each result's node type. Results with missing or zero coordinates
     * are skipped.
     */
    private readonly _updateMapForWarehouses = () => {
        if (!this._isWarehouseMapOwner()) {
            return;
        }

        const pushpinOptions = this.props.config.pushpinOptions;
        const warehouseLocatorStateManager = this.props.data.warehouseLocatorStateManager?.result;
        const nodes = warehouseLocatorStateManager?.nodes;
        const selectedNodeKey = warehouseLocatorStateManager?.selectedNodeKey;

        if (this.map && nodes) {
            let mapLocation: ICrossEntityInventoryNode | undefined;
            this.map.entities.clear();
            const pushpins: Microsoft.Maps.IPrimitive[] = [];

            for (const [index, node] of nodes.entries()) {
                if (node.latitude && node.longitude) {
                    const nodeKey = getCrossEntityNodeKey(node);
                    const isSelectedLocation = !!selectedNodeKey && selectedNodeKey === nodeKey;
                    const options = this._getPushpinOptions(
                        isSelectedLocation,
                        index,
                        pushpinOptions,
                        node.orgUnitName,
                        node.nodeType ?? CrossEntityNodeType.warehouse
                    );
                    const pushpin = new Microsoft.Maps.Pushpin(new Microsoft.Maps.Location(node.latitude, node.longitude), options);

                    Microsoft.Maps.Events.addHandler(pushpin, 'click', () => {
                        this.handleWarehousePushpinClick(nodeKey);
                    });
                    isSelectedLocation ? pushpins.unshift(pushpin) : pushpins.push(pushpin);

                    if (isSelectedLocation) {
                        mapLocation = node;
                    }
                }
            }

            if (mapLocation) {
                this.map.setView({ center: new Microsoft.Maps.Location(mapLocation.latitude!, mapLocation.longitude!) });
            } else if (ArrayExtensions.hasElements(pushpins)) {
                this.map.setView({ bounds: Microsoft.Maps.LocationRect?.fromShapes(pushpins) });
            }

            this.map.setOptions({ pushpinAccessible: true } as Microsoft.Maps.IMapOptions);
            this.map.entities.push(pushpins);
        }
    };

    /**
     * Handle click event.
     * @param location - Organization unit location.
     */
    private readonly handleClickEvent = (location: OrgUnitLocation | undefined): void => {
        const storeSelectorStateManager = this.props.data.storeSelectorStateManager.result;
        storeSelectorStateManager?.setSelectedStoreLocationId(undefined);
        if (location?.OrgUnitNumber) {
            setTimeout(() => {
                storeSelectorStateManager?.setSelectedStoreLocationId(
                    location.OrgUnitNumber,
                    location.DataAreaId,
                    location.InventoryLocationId
                );
            }, this.timeout);
        }
    };

    /**
     * Handle the click event of the pushpin for distributors on the maps.
     * @param recordId - RecordId.
     */
    private readonly handleDistributorPushpinClick = (recordId: number | undefined): void => {
        const distributorSelectorStateManager = this.props.data.distributorSelectorStateManager?.result;
        // Show the distributor details that appear below the screen size of 768px.
        distributorSelectorStateManager?.updateDistributorMapVisibility(true);
        distributorSelectorStateManager?.setSelectedDistributorId(undefined);
        if (recordId) {
            setTimeout(() => {
                distributorSelectorStateManager?.setSelectedDistributorId(recordId);
            }, this.timeout);
        }
    };

    /**
     * Handle the click event of a warehouse pushpin.
     * @param nodeKey - Cross-legal-entity key of the clicked warehouse.
     */
    private readonly handleWarehousePushpinClick = (nodeKey: string | undefined): void => {
        const warehouseLocatorStateManager = this.props.data.warehouseLocatorStateManager?.result;
        void warehouseLocatorStateManager?.setSelectedNodeKey(undefined);
        if (nodeKey) {
            setTimeout(() => {
                void warehouseLocatorStateManager?.setSelectedNodeKey(nodeKey);
            }, this.timeout);
        }
    };

    /**
     * Whether this map instance is free to serve the warehouse locator.
     *
     * Every map instance on the page resolves the shared warehouse-locator state manager, so a map
     * owned by the store or distributor selector would otherwise react to warehouse state and clear
     * its own pushpins.
     * @returns Whether no other selector currently owns this map.
     */
    private readonly _isWarehouseMapOwner = (): boolean => {
        const { data } = this.props;

        return (
            !data.distributorSelectorStateManager?.result?.isDistributorSelectorDialogOpen &&
            !data.storeSelectorStateManager?.result?.isDialogOpen
        );
    };

    /**
     * Whether this map instance should currently be plotting warehouse-locator results.
     * @returns Whether warehouse results should take precedence on this map.
     */
    private readonly _isWarehouseMapActive = (): boolean => {
        return this._isWarehouseMapOwner() && ArrayExtensions.hasElements(this.props.data.warehouseLocatorStateManager?.result?.nodes);
    };

    /**
     * Get the pushpin option.
     * @param isSelectedLocation - Is selected location.
     * @param index - Index.
     * @param pushpinOptions - PushpinOptions.
     * @returns - The pushpin options.
     */
    private readonly _getPushpinOptions = (
        isSelectedLocation: boolean,
        index: number,
        pushpinOptions?: IPushpinOptionsData,
        locationName?: string,
        nodeType?: CrossEntityNodeType
    ) => {
        const text = nodeType === undefined && pushpinOptions?.showIndex ? (index + 1).toString() : undefined;

        const size = pushpinOptions?.size || 1;
        const color = isSelectedLocation ? pushpinOptions?.selectionColor || pushpinOptions?.color : pushpinOptions?.color;
        const icon = this._getSvgIcon(size, color, text, nodeType);

        return {
            // Fallback if icon doesn't render properly
            color: isSelectedLocation ? pushpinOptions?.selectionColor : pushpinOptions?.color,
            icon,
            title: locationName ?? `${index + 1}`
        };
    };

    private readonly _getSvgIcon = (size: number, color?: string, text?: string, nodeType?: CrossEntityNodeType) => {
        const baseValue: number = 32;
        const markerType = nodeType === CrossEntityNodeType.retailStore ? 'store' : 'warehouse';
        const nodeIcon =
            markerType === 'store'
                ? '<path fill="#ffffff" d="M96 174h174l-12 56H108l-12-56zm22 70h132v92H118v-92zm25 23v69h62v-69h-62z" />'
                : '<path fill="#ffffff" d="M91 224l92-65 92 65v112h-34v-73H125v73H91V224zm58 68h68v44h-68v-44z" />';
        return `<svg xmlns="http://www.w3.org/2000/svg" width="${baseValue * size}" height="${baseValue * size}" viewBox="0 0 365 560"${
            nodeType === undefined ? '' : ` data-node-type="${markerType}"`
        }>
                    <path fill="${color}" d="M182.9,551.7c0,0.1,0.2,0.3,0.2,0.3S358.3,283,358.3,194.6c0-130.1-88.8-186.7-175.4-186.9 C96.3,7.9,7.5,64.5,7.5,194.6c0,88.4,175.3,357.4,175.3,357.4S182.9,551.7,182.9,551.7z" />
                    ${
                        nodeType === undefined
                            ? text
                                ? `<text x="185" y="280" style="font-size:220px;fill:#ffffff;" text-anchor="middle">${text}</text>`
                                : ''
                            : nodeIcon
                    }
                </svg>`;
    };
}

export default Map;
